import { isVideoAssetUrl } from "@/lib/utils/apex-news";

export function containsMediaNode(node: unknown): boolean {
  if (!node || typeof node !== "object") {
    return false;
  }

  const maybeNode = node as {
    type?: string;
    tagName?: string;
    children?: unknown[];
  };

  if (
    maybeNode.type === "element" &&
    (maybeNode.tagName === "img" || maybeNode.tagName === "video")
  ) {
    return true;
  }

  if (!Array.isArray(maybeNode.children)) {
    return false;
  }

  for (const child of maybeNode.children) {
    if (containsMediaNode(child)) {
      return true;
    }
  }

  return false;
}

export function extractFirstMediaSrcFromNode(node: unknown): string | null {
  if (!node || typeof node !== "object") {
    return null;
  }

  const maybeNode = node as {
    type?: string;
    tagName?: string;
    properties?: { src?: unknown };
    children?: unknown[];
  };

  if (maybeNode.type === "element" && maybeNode.tagName === "img") {
    const src = maybeNode.properties?.src;
    if (typeof src === "string" && src.length > 0) {
      return src;
    }
  }

  if (!Array.isArray(maybeNode.children)) {
    return null;
  }

  for (const child of maybeNode.children) {
    const found = extractFirstMediaSrcFromNode(child);
    if (found) {
      return found;
    }
  }

  return null;
}

export function isLikelyImagePreviewUrl(url: string | null | undefined): boolean {
  if (!url) {
    return false;
  }
  if (isVideoAssetUrl(url)) {
    return false;
  }
  const lower = url.toLowerCase();
  return (
    lower.includes(".jpg") ||
    lower.includes(".jpeg") ||
    lower.includes(".png") ||
    lower.includes(".webp") ||
    lower.includes(".gif") ||
    lower.includes(".avif")
  );
}

export function getYouTubeEmbedUrl(url: string | null | undefined): string | null {
  if (!url) {
    return null;
  }

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();

    if (host === "youtu.be") {
      const id = parsed.pathname.replace(/^\/+/, "");
      return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
    }

    if (
      host === "youtube.com" ||
      host.endsWith(".youtube.com") ||
      host === "youtube-nocookie.com" ||
      host.endsWith(".youtube-nocookie.com")
    ) {
      if (parsed.pathname === "/watch") {
        const id = parsed.searchParams.get("v");
        return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
      }

      if (parsed.pathname.startsWith("/shorts/")) {
        const id = parsed.pathname.replace("/shorts/", "").split("/")[0];
        return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
      }

      if (parsed.pathname.startsWith("/embed/")) {
        const id = parsed.pathname.replace("/embed/", "").split("/")[0];
        return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
      }
    }
  } catch {
    return null;
  }

  return null;
}

export function getYouTubeWatchUrl(url: string | null | undefined): string | null {
  if (!url) {
    return null;
  }

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();

    if (host === "youtu.be") {
      const id = parsed.pathname.replace(/^\/+/, "");
      return id ? `https://www.youtube.com/watch?v=${id}` : null;
    }

    if (
      host === "youtube.com" ||
      host.endsWith(".youtube.com") ||
      host === "youtube-nocookie.com" ||
      host.endsWith(".youtube-nocookie.com")
    ) {
      if (parsed.pathname === "/watch") {
        const id = parsed.searchParams.get("v");
        return id ? `https://www.youtube.com/watch?v=${id}` : null;
      }

      if (parsed.pathname.startsWith("/shorts/")) {
        const id = parsed.pathname.replace("/shorts/", "").split("/")[0];
        return id ? `https://www.youtube.com/watch?v=${id}` : null;
      }

      if (parsed.pathname.startsWith("/embed/")) {
        const id = parsed.pathname.replace("/embed/", "").split("/")[0];
        return id ? `https://www.youtube.com/watch?v=${id}` : null;
      }
    }
  } catch {
    return null;
  }

  return null;
}

export function getHeadingAnchorId(
  node: unknown,
  headingAnchorIdByLine: Map<number, string>,
): string | undefined {
  if (!node || typeof node !== "object") {
    return undefined;
  }
  const maybePosition = (node as { position?: { start?: { line?: number } } }).position;
  const line = maybePosition?.start?.line;
  if (typeof line !== "number") {
    return undefined;
  }
  return headingAnchorIdByLine.get(line);
}
